import {
  Badge,
  Center,
  Spinner,
  Tab,
  TabList,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { UserScopeEnum } from "@src/__generated__/graphql";
import { COMMUNICATION_TABS } from "@src/constants/tasks";
import { useStore } from "@src/utils/hooks";
import useIntersectionObserver from "@src/utils/hooks/useIntersectionObserver";
import { getQueryParamOptional } from "@src/utils/routing";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { Fragment, useEffect, useRef } from "react";
import { COMMUNICATION_TAB_QUERY_KEY } from "./communication-tab-query";

export const CommunicationTabParent = observer(
  function CommunicationTabParent() {
    const { taskDetailModalStore } = useStore();
    const commentsLoaderRef = useRef(null);
    const { communicationTabParentStore: store, activeTabId } =
      taskDetailModalStore;
    const { query } = useRouter();
    const commTabQueryValue = getQueryParamOptional(
      COMMUNICATION_TAB_QUERY_KEY,
      query,
    );

    useEffect(() => {
      if (!commTabQueryValue) return;

      const tabIndex = store.availableTabs.findIndex(
        (tab) => tab.userScope === commTabQueryValue,
      );
      if (tabIndex === -1 || tabIndex === store.selectedTab) return;
      store.setSelectedTab(tabIndex);
    }, [
      commTabQueryValue,
      store.availableTabs.length,
      store.setSelectedTab,
      store.selectedTab,
    ]);

    useIntersectionObserver({
      threshold: 0.25,
      target: commentsLoaderRef,
      onIntersect: () => {
        if (!store.pagination.hasNextPage && store.pagination.currentPage !== 0)
          return;
        if (store.commentsFetcher.isLoading.value) return;
        if (store.lastSeenCommentMutator.isLoading.value) return;
        store.fetchTaskComments(store.pagination);
      },
    });

    if (
      taskDetailModalStore.isDrawerMode &&
      !COMMUNICATION_TABS.includes(activeTabId.value)
    )
      return;

    return (
      <Tabs
        h="full"
        p="0"
        index={store.selectedTab}
        onChange={store.setSelectedTab}
      >
        {store.availableTabs.length > 1 && (
          <TabList px="8">
            {store.availableTabs.map((tab) => {
              const clientCommentsCount =
                taskDetailModalStore.task.value?.clientCommentCount ?? 0;
              const partnerCommentsCount =
                taskDetailModalStore.task.value?.partnerCommentCount ?? 0;
              const internalCommentsCount =
                (taskDetailModalStore.task.value?.commentCount ?? 0) -
                clientCommentsCount -
                partnerCommentsCount;
              const badgeCount =
                tab.userScope === undefined
                  ? internalCommentsCount
                  : tab.userScope === UserScopeEnum.Client
                    ? clientCommentsCount
                    : partnerCommentsCount;

              return (
                <Tab key={`tab-${tab.id}`}>
                  {tab.label()}
                  {!!badgeCount && (
                    <Badge ml="1" colorScheme="grey">
                      {badgeCount}
                    </Badge>
                  )}
                </Tab>
              );
            })}
          </TabList>
        )}
        <TabPanels>
          {store.availableTabs.map((tab) => (
            <Fragment key={`tab-panel-${tab.id}`}>{tab.component}</Fragment>
          ))}
        </TabPanels>
        <Center ref={commentsLoaderRef} h="10" minH="10">
          {store.pagination.canLoadMore && <Spinner />}
        </Center>
      </Tabs>
    );
  },
);
